'use client';

import { ReactNode, useCallback } from 'react';
import { EntityRatingCommentCount, Partner, PartnerScheduleSearchResult } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { PartnerHeader } from './partner-header.component';
import { PartnerProfileHeader } from './partner-profile-header.component';
import { PartnerAsideCard } from './partner-aside-card.component';
import { PartnerSchedulesSection } from './partner-schedules-section.component';
import { PartnerProfileLoading } from './partner-profile-loading.component';
import { PartnerAboutSection } from './partner-about-section.component';
import { PartnerRatingSection } from './partner-rating-section.component';
import PartnerNotFound from './partner-not-found.component';
import { TabPartner } from './tab-partner.component';

import { Breadcrumb } from '@/components/ui/breadcrumb.component';
import { Card } from '@/components/ui/card.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { resolvePartnerName } from '@/utils/partner/partner.util';
import { ErrorBoundary } from '@/app/_components/error-boundary.component';
import { EntityCommentsSort } from '@/app/(main)/(pages)/_components/entities/entity-comments-sort.component';
import { EntityProcedureAnchorButton } from '@/app/(main)/(pages)/_components/entities/entity-procedure/_components/entity-procedure-anchor-button.component';
import { ENTITY_PROCEDURE_ASIDE_SECTION_ID } from '@/app/(main)/(pages)/_components/entities/entity-procedure/entity-procedure.const';
import { EntityScheduleRedirectButton } from '@/app/(main)/(pages)/_components/entities/entity-schedule-redirect-button.component';
import { EntityActionLoading } from '@/app/(main)/(pages)/_components/entities/entity-action-loading.component';
import { EntitySectionDivider } from '@/app/(main)/(pages)/_components/entities/entity-section-divider.component';
import { useEntityComments } from '@/app/(main)/(pages)/_components/entities/hooks/use-entity-comments.hook';
import { useEntityProfile } from '@/app/(main)/(pages)/_components/entities/hooks/use-entity-profile.hook';
import { HttpException } from '@/utils/api/api-client';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { usePartnerProcedures } from '@/app/(main)/(pages)/(institucional)/(index)/parceiros/_hooks/use-partner-procedures.hook';
import {
  getPartnerProfileComments,
  getPartnerProfileFromSearchApi,
  PartnerProfileFromSearchApi,
} from '@/app/(main)/(pages)/(institucional)/(index)/parceiros/_services/get-partner-profile-from-search-api.service';
import { SearchApiRatingCommentsOrderBy } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

function isNotFoundError(error: unknown): boolean {
  return error instanceof HttpException && error.status === 404;
}

interface PartnerWrapperProps {
  slug: string;
  partner: Partner;
  aboutSection: ReactNode;
  ratingSection: ReactNode;
  professionalsSection?: ReactNode;
  commentsCount?: EntityRatingCommentCount | null;
  inPersonSchedules?: PartnerScheduleSearchResult[];
  telehealthSchedules?: PartnerScheduleSearchResult[];
}

export function PartnerWrapper({
  slug,
  aboutSection,
  ratingSection,
  professionalsSection,
  commentsCount,
  partner: partnerFromBff,
  inPersonSchedules,
  telehealthSchedules,
}: PartnerWrapperProps) {
  const { value: newScheduleEnabled, loading: isFlagLoading } = useFeatureFlag('newSchedule');
  const usesNewSchedule = !isFlagLoading && newScheduleEnabled;

  const { profile, status, error } = useEntityProfile<PartnerProfileFromSearchApi>(slug, {
    fetchProfile: getPartnerProfileFromSearchApi,
    fallbackProfile: () => ({
      partner: partnerFromBff,
      commentsCount: commentsCount ?? { total: 0, comments: [] },
      initialComments: [],
      hasMoreComments: false,
    }),
    loggerContext: 'partner.profile-view',
    enabled: usesNewSchedule,
  });

  const usesSearchApiProfile = usesNewSchedule && status === 'ready' && Boolean(profile);
  const partner = profile?.partner ?? partnerFromBff;
  const partnerId = partner.id ? String(partner.id) : '';
  const partnerFacilityId = String(partner.facilities?.[0]?.id ?? '');
  const partnerProcedures = usePartnerProcedures(partnerId, {
    enabled: usesNewSchedule && status === 'ready' && Boolean(partnerId),
  });
  const hasSchedulesSection = partnerProcedures.isLoading || partnerProcedures.availableChannels.length > 0;
  const showsSchedulesSection = usesNewSchedule && hasSchedulesSection;

  const {
    orderBy,
    comments,
    hasMore: hasMoreComments,
    commentsKey,
    isSorting: isSortingComments,
    changeOrderBy,
  } = useEntityComments(
    useCallback(
      (page: number, nextOrderBy: SearchApiRatingCommentsOrderBy) =>
        getPartnerProfileComments(partnerFacilityId, page, nextOrderBy),
      [partnerFacilityId],
    ),
    profile?.initialComments ?? [],
    Boolean(profile?.hasMoreComments),
    { context: 'partner.comments', entity: { partner_facility_id: partnerFacilityId } },
    AnalyticsEnum.PARTNER_PROFILE_SORT_COMMENTS,
  );

  const loadCommentsPage = useCallback(
    (page: number) => getPartnerProfileComments(partnerFacilityId, page, orderBy),
    [partnerFacilityId, orderBy],
  );

  const schedulesSection = showsSchedulesSection
    ? (
        <div className="xl:hidden">
          <EntitySectionDivider />
          <PartnerSchedulesSection
            procedures={partnerProcedures}
            partnerId={String(partner.id)}
            className="rounded-none border-0 px-0 py-4"
          />
        </div>
      )
    : null;

  const resolvedScheduleAction = showsSchedulesSection
    ? (
        <EntityProcedureAnchorButton
          className="flex-1 min-w-0 h-10 text-base"
          label="Atendimentos"
          data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
        />
      )
    : partnerId && (
      <EntityScheduleRedirectButton
        path={`/parceiros/${partnerId}/`}
        label="Serviços e horários"
        dataTestId={DataTestIdEnum.PARTNER_PROFILE_SCHEDULE_BUTTON_HEADER}
        analyticsEvent={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
        className="flex-1 min-w-0 h-10 text-base"
      />
    );

  const scheduleAction = isFlagLoading ? <EntityActionLoading /> : resolvedScheduleAction;

  const breadcrumbsItems = [
    {
      label: 'Rede de atendimento',
      href: '/rede-de-atendimento/',
    },
    {
      label: resolvePartnerName(partner),
    },
  ];

  if (usesNewSchedule && status === 'loading') {
    return <PartnerProfileLoading />;
  }

  if (usesNewSchedule && status === 'error' && isNotFoundError(error)) {
    return <PartnerNotFound />;
  }

  return (
    <>
      <Breadcrumb list={breadcrumbsItems} className="hidden sm:block" />
      <Breadcrumb list={breadcrumbsItems} abbreviated className="sm:hidden" />

      <ErrorBoundary loggerContext="partner.wrapper">
        <ContentContainer className="px-0 md:py-6 bg-white md:bg-transparent">
          <div className="block mx-auto sm:max-w-lg md:max-w-full md:w-auto xl:grid xl:grid-flow-col xl:auto-cols-fr xl:items-start xl:gap-6">
            <section>
              <Card className="border-0 md:border border-gray-200 md:px-10 pt-6 pb-6 md:pt-8 md:pb-8 3xl:pt-10 3xl:pb-10 w-full">
                {usesSearchApiProfile && profile
                  ? (
                      <PartnerProfileHeader
                        partner={partner}
                        commentsCount={profile.commentsCount}
                        scheduleAction={scheduleAction}
                      />
                    )
                  : (
                      <>
                        <PartnerHeader
                          partner={partner}
                          commentsCount={commentsCount}
                          scheduleAction={scheduleAction}
                        />
                        <hr className="mb-3 mt-8 md:hidden" />
                      </>
                    )}

                <TabPartner
                  about={usesSearchApiProfile ? <PartnerAboutSection partner={partner} /> : aboutSection}
                  rating={usesSearchApiProfile && profile
                    ? (
                        <PartnerRatingSection
                          partner={partner}
                          countComments={profile.commentsCount}
                          initialComments={comments}
                          loadCommentsPage={loadCommentsPage}
                          hasMoreComments={hasMoreComments}
                          commentsKey={commentsKey}
                          isSortingComments={isSortingComments}
                          commentsSort={(
                            <EntityCommentsSort
                              orderBy={orderBy}
                              onOrderByChange={changeOrderBy}
                              disabled={isSortingComments}
                            />
                          )}
                        />
                      )
                    : ratingSection}
                  professionals={usesSearchApiProfile ? null : professionalsSection}
                  schedules={schedulesSection}
                />
              </Card>
            </section>

            {usesSearchApiProfile
              ? showsSchedulesSection && (
                <PartnerSchedulesSection
                  procedures={partnerProcedures}
                  partnerId={String(partner.id)}
                  className="hidden w-full xl:flex xl:sticky xl:top-6"
                  scrollTargetId={ENTITY_PROCEDURE_ASIDE_SECTION_ID}
                />
              )
              : (
                  <PartnerAsideCard
                    partner={partner}
                    inPersonSchedules={inPersonSchedules}
                    telehealthSchedules={telehealthSchedules}
                    className="hidden xl:flex border-gray-200"
                  />
                )}
          </div>
        </ContentContainer>
      </ErrorBoundary>
    </>
  );
}
